import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import type { AuthenticatedUser, Permission, UserRole } from '@bakery/shared';
import { DEFAULT_STAFF_PERMISSIONS, relativeTime } from '@bakery/shared';

import { ApiError, api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Modal,
  SectionHeading,
  Select,
  Skeleton,
  cx,
  useToast,
} from '../components/ui.js';

type UserRow = AuthenticatedUser & { employeeName: string | null };

export function UsersTab() {
  const { user: me } = useAuth();
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<UserRow | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: api.users.list });

  if (users.isLoading) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <SectionHeading
          title="Logins"
          hint="Who can sign in, and what each of them is allowed to see."
          action={
            <Button size="sm" icon={<UserPlus className="size-4" />} onClick={() => setCreating(true)}>
              Add user
            </Button>
          }
        />

        <ul className="divide-y divide-[var(--color-line)]">
          {users.data?.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-3 py-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] text-sm font-semibold text-[var(--color-accent-ink)]">
                {row.displayName.slice(0, 1).toUpperCase()}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cx('text-sm font-medium', !row.isActive && 'line-through opacity-50')}>
                    {row.displayName}
                  </span>
                  <Badge tone={row.role === 'admin' ? 'brand' : 'neutral'}>
                    {row.role === 'admin' ? 'Administrator' : 'Staff'}
                  </Badge>
                  {row.mustChangePassword && <Badge tone="warn">Password not set</Badge>}
                  {row.id === me?.id && <Badge tone="info">You</Badge>}
                </div>
                <p className="truncate text-xs text-[var(--color-ink-faint)]">
                  {row.loginId} · {row.email}
                  {row.employeeName && ` · payroll: ${row.employeeName}`}
                  {row.lastLoginAt && ` · last in ${relativeTime(row.lastLoginAt)}`}
                </p>
              </div>

              <div className="flex shrink-0 gap-1">
                <Button size="sm" variant="ghost" onClick={() => setResetting(row)} icon={<KeyRound className="size-4" />}>
                  Password
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                  Edit
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {(creating || editing) && (
        <UserModal
          user={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

function UserModal({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user: me, can } = useAuth();

  const catalogue = useQuery({ queryKey: ['permission-catalogue'], queryFn: api.auth.permissionCatalogue });
  const employees = useQuery({ queryKey: ['employees'], queryFn: () => api.staff.employees(), enabled: can('staff.view') });

  const [form, setForm] = useState({
    email: user?.email ?? '',
    loginId: user?.loginId ?? '',
    displayName: user?.displayName ?? '',
    role: (user?.role ?? 'staff') as UserRole,
    employeeId: user?.employeeId ?? '',
    isActive: user?.isActive ?? true,
    password: '',
  });

  const [permissions, setPermissions] = useState<Permission[]>(
    user ? user.permissions : [...DEFAULT_STAFF_PERMISSIONS],
  );

  const toggle = (permission: Permission) =>
    setPermissions((current) =>
      current.includes(permission) ? current.filter((p) => p !== permission) : [...current, permission],
    );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        email: form.email,
        loginId: form.loginId,
        displayName: form.displayName,
        role: form.role,
        permissions,
        employeeId: form.employeeId || null,
      };
      return user
        ? api.users.update(user.id, { ...body, isActive: form.isActive })
        : api.users.create({ ...body, password: form.password });
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.push({
        tone: 'ok',
        title: `${saved.displayName} saved`,
        body: user ? undefined : 'They will be asked to set their own password when they first sign in.',
      });
      onClose();
    },
    onError: (error: Error) =>
      toast.push({ tone: 'danger', title: 'Could not save', body: error instanceof ApiError ? error.message : error.message }),
  });

  const remove = useMutation({
    mutationFn: () => api.users.remove(user!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.push({ tone: 'ok', title: 'User removed' });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove', body: error.message }),
  });

  const isSelf = user?.id === me?.id;
  const ready = form.email && form.loginId && form.displayName && (user || form.password.length >= 8);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={user ? user.displayName : 'New user'}
      description={user ? `${user.loginId} · ${user.email}` : 'They set their own password the first time they sign in.'}
      footer={
        <>
          {user && !isSelf && (
            <Button variant="ghost" icon={<Trash2 className="size-4" />} loading={remove.isPending} onClick={() => remove.mutate()}>
              Delete
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Full name" required>
            <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} autoFocus />
          </Field>
          <Field label="Login ID" required hint="Short handle they can type instead of the email.">
            <Input value={form.loginId} onChange={(e) => setForm({ ...form, loginId: e.target.value })} />
          </Field>
          <Field label="Email" required>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          {!user && (
            <Field label="Starting password" required hint="At least 8 characters. They must change it at first sign-in.">
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Account type">
            <Select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
              disabled={isSelf}
            >
              <option value="staff">Staff — only what you tick below</option>
              <option value="admin">Administrator — everything</option>
            </Select>
          </Field>
          <Field label="Payroll record" hint="Links this login to a staff member so they can see their own payslip.">
            <Select value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
              <option value="">Not linked</option>
              {employees.data?.map((employee) => (
                <option key={employee.id} value={employee.id}>{employee.displayName}</option>
              ))}
            </Select>
          </Field>
        </div>

        {user && !isSelf && (
          <label className="flex items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="size-4 accent-[var(--color-crust-600)]"
            />
            Account is switched on
          </label>
        )}

        {/* Permissions ------------------------------------------------- */}
        {form.role === 'admin' ? (
          <p className="flex gap-2 rounded-[var(--radius-md)] bg-[var(--color-accent-soft)] px-3 py-2.5 text-sm text-[var(--color-accent-ink)]">
            <ShieldCheck className="mt-px size-4 shrink-0" />
            An administrator can see and do everything, including takings, margins, all payslips and user management.
          </p>
        ) : (
          <div>
            <div className="mb-2 flex items-end justify-between">
              <p className="text-sm font-medium">What they can see and do</p>
              <button
                onClick={() => setPermissions([...DEFAULT_STAFF_PERMISSIONS])}
                className="text-xs text-crust-600 hover:underline"
              >
                Reset to the usual staff set
              </button>
            </div>

            <div className="space-y-3">
              {catalogue.data?.groups.map((group) => (
                <div key={group.label} className="rounded-[var(--radius-md)] border border-[var(--color-line)] p-3">
                  <p className="mb-2 text-xs font-semibold tracking-wide text-[var(--color-ink-soft)] uppercase">
                    {group.label}
                  </p>
                  <div className="space-y-2">
                    {group.permissions.map((permission) => (
                      <label key={permission.key} className="flex gap-2.5">
                        <input
                          type="checkbox"
                          checked={permissions.includes(permission.key)}
                          onChange={() => toggle(permission.key)}
                          className="mt-0.5 size-4 shrink-0 accent-[var(--color-crust-600)]"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm">{permission.label}</span>
                          <span className="block text-xs text-[var(--color-ink-faint)]">{permission.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState('');

  const reset = useMutation({
    mutationFn: () => api.users.resetPassword(user.id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.push({
        tone: 'ok',
        title: `Password set for ${user.displayName}`,
        body: 'They will be asked to choose their own the next time they sign in.',
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not reset', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Reset password — ${user.displayName}`}
      description="This signs them out everywhere and makes them choose a new password."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={reset.isPending} disabled={password.length < 8} onClick={() => reset.mutate()}>
            Set password
          </Button>
        </>
      }
    >
      <Field label="Temporary password" hint="Tell it to them in person. They will replace it at their next sign-in.">
        <Input autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
    </Modal>
  );
}
